import MfButton from '@components/branded/MfButton'
import _ from 'lodash'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import * as Icon from 'react-feather'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Card, CardHeader, CardBody, Col, Row } from 'reactstrap'
import { change, Field, formValueSelector, reduxForm } from 'redux-form'
import { verifyUserByEmailAndClientId, createAdmin, fetchClientPortals, createUser } from '../../actions'
import { fetchLocationOptions } from '../../redux/storeConfig/slices/slice.locations'
import DropZoneField from '../../components/dropzone/dropzoneField'
import BackButton from '../general/BackButton'
import {
  renderInput,
  renderTzSelect,
  renderTooltip,
  DropdownListNew2,
  ReduxFormSwitch as Switch,
  renderDropdownListNew
} from '../general/FormInputs'

export const timeZones = () => {
  const timeZones = moment.tz.names()
  const offsetTmz = timeZones.map(tzName => {
    const offset = moment.tz(tzName).format('Z').replace(':00', '').replace(':30', '.5')
    const offsetValue = offset === 0 ? 0 : parseFloat(offset).toFixed(2)

    return {
      label: `(GMT${moment.tz(tzName).format('Z')}) ${tzName}`,
      value: tzName,
      time: `${offsetValue}`
    }
  })

  return _.sortBy(offsetTmz, el => -el.time)
}

const emptyArray = []

/**
 * AdminForm component used on /organization_admins/create and /admins/create
 *
 * @param {Object} props - The properties of the component
 * @param {Function} props.fetchClientPortals - The function to fetch client portals
 * @param {Function} props.createUser - The function to create a user
 * @param {Function} props.createAdmin - The function to create an admin
 * @param {Function} props.fetchLocationOptions - The function to fetch location options
 * @param {Function} props.changeFieldValue - The function to change a field value
 * @param {Object} props.values - The values of the form
 * @param {Array} props.roles - The roles of the form
 * @param {Array} props.clients - The clients of the form
 * @param {Array} props.client_portals - The client portals of the form
 * @param {Array} props.locations - The locations of the form
 * @param {Object} props.currentAdmin - The current admin
 * @param {Object} props.history - The history object
 * @param {Function} props.handleSubmit - The function to handle the submit
 * @param {Array} props.errors - The errors of the form
 * @param {Function} props.change - The function to change a value
 * @param {Function} props.setLoading - The function to set the loading state
 * @param {Function} props.verifyUserByEmailAndClientId - The function to verify a user by email and client ID
 * @param {Function} props.fetchClients - The function to fetch clients
 * @param {Function} props.fetchAllRoles - The function to fetch all roles
 * @param {Function} props.fetchOrganizationRoles - The function to fetch organization roles
 * @param {Function} props.fetchPortals - The function to fetch portals
 *
 * @returns {JSX.Element} - The JSX element
 */
const AdminForm = props => {
  const [imageFile, setImageFile] = useState([])
  const [emailExists, setEmailExists] = useState(false)
  const [emailAndClientExists, setEmailAndClientExists] = useState(false)
  const [disableCustomerNotification, setDisableCustomerNotification] = useState(false)
  const [customerNotificationMessage, setCustomerNotificationMessage] = useState(null)
  const prevPortals = useRef([])

  const loggedInAs = props.currentAdmin.logged_in_as

  const { has_api_access, client, portals, locations, email, is_client_admin } = props.values
  const { change, changeFieldValue, fetchClientPortals, fetchLocationOptions, client_portals } = props

  // when selected client changes, update the selected portals
  useEffect(() => {
    if (client) {
      fetchClientPortals(client.id)
      changeFieldValue('adminForm', 'portals', null)
    }
  }, [client, changeFieldValue, fetchClientPortals])

  // when selected portals change, fetch new locations for those portals
  useEffect(() => {
    if (!portals?.length) {
      return
    }
    // do two things:
    // 1. remove any selected locations that are not in the selected portals
    // 2. update the available location options based on the selected portals,
    // being sure to not include any locations that are already selected
    const selectedLocations = locations || []
    const updatedLocations = selectedLocations.filter(location => {
      return portals.some(portal => portal.value === location.portal_id)
    })
    const selectedPortalsLength = portals.length
    if (selectedPortalsLength > 1) {
      setDisableCustomerNotification(true)
      changeFieldValue('adminForm', 'send_creation_email', false)
      setCustomerNotificationMessage(
        'To avoid multiple emails being sent to the customer due to the selection of multiple portals, the notification feature has been disabled. As such any notifications for account creation must be handled manually.'
      )
    } else {
      setDisableCustomerNotification(false)
      changeFieldValue('adminForm', 'send_creation_email', true)
      setCustomerNotificationMessage(null)
    }

    if (!_.isEqual(selectedLocations, updatedLocations)) {
      changeFieldValue('adminForm', 'locations', updatedLocations)
    }

    if (loggedInAs === 'Organization') {
      return
    }

    if (!_.isEqual(prevPortals.current, portals)) {
      prevPortals.current = portals
      const portalIds = portals.map(portal => portal.id)
      fetchLocationOptions({ portal_id: portalIds })
    }
  }, [portals, fetchLocationOptions, changeFieldValue, loggedInAs, locations])

  // when email changes, check if email exists and if email and client exists
  useEffect(() => {
    const checkEmailAndClientExists = (email, clientId) => {
      if (email && clientId) {
        verifyUserByEmailAndClientId(email, clientId)
          .then(response => {
            const emailAndClientExists = response.exists && response.clientId === clientId
            setEmailExists(response.exists)
            setEmailAndClientExists(emailAndClientExists)
            changeFieldValue('adminForm', 'linked_to_user', response.isLinkedToCustomer)
          })
          .catch(error => console.error('Error verifying email and client:', error))
      } else {
        setEmailExists(false)
        setEmailAndClientExists(false)
        changeFieldValue('adminForm', 'linked_to_user', false)
      }
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (emailRegex.test(email)) {
      checkEmailAndClientExists(email, client?.id)
    }
  }, [email, client, changeFieldValue])

  // when is_client_admin changes, reset client, portals, and locations
  useEffect(() => {
    if (!is_client_admin) {
      changeFieldValue('adminForm', 'client', null)
      setEmailAndClientExists(false)

      changeFieldValue('adminForm', 'portals', [])
      changeFieldValue('adminForm', 'locations', [])
    }
  }, [is_client_admin, changeFieldValue])

  const handleOnDrop = (newImageFile, onChange) => {
    if (newImageFile.length !== 0) {
      const imageFile = {
        file: newImageFile[0],
        name: newImageFile[0].name,
        preview: URL.createObjectURL(newImageFile[0]),
        size: newImageFile[0].size
      }

      setImageFile([imageFile])
      onChange(imageFile)
    } else {
      alert("Can't drop multiple files.")
    }
  }

  const clearImage = () => {
    setImageFile([])
  }

  const appendIfExists = (formData, key, value) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value)
    }
  }

  const createFormData = (reduxFormValues, propsValues) => {
    const formData = new FormData()

    // Role IDs
    if (propsValues.role_ids?.length > 0) {
      formData.append('role_ids', JSON.stringify(propsValues.role_ids.map(r => (typeof r === 'number' ? r : r.id))))
    }

    // Required fields
    formData.append('name', reduxFormValues.name)
    formData.append('email', reduxFormValues.email)
    formData.append('password', reduxFormValues.user_security_field)
    formData.append('password_confirmation', reduxFormValues.user_security_field_confirmation)

    // Optional fields
    appendIfExists(formData, 'timezone', reduxFormValues.timezone?.value)
    appendIfExists(formData, 'avatar', reduxFormValues.avatar?.file)
    appendIfExists(formData, 'is_active', reduxFormValues.is_active)
    appendIfExists(formData, 'has_api_access', reduxFormValues.has_api_access)
    appendIfExists(formData, 'has_agency_api_access', reduxFormValues.has_agency_api_access)

    // Client Admin specific fields
    if (reduxFormValues.is_client_admin) {
      formData.append('is_client_admin', reduxFormValues.is_client_admin)
      formData.append('client_id', reduxFormValues.client.id)
      appendIfExists(formData, 'linked_to_user', reduxFormValues.linked_to_user)
      appendIfExists(formData, 'send_creation_email', reduxFormValues.send_creation_email)
      appendIfExists(formData, 'portals', reduxFormValues.portals)
      appendIfExists(formData, 'locations', reduxFormValues.locations)
    } else {
      formData.append('is_client_admin', false)
    }

    return formData
  }

  const onSubmit = reduxFormValues => {
    props.setLoading(true)
    const formData = createFormData(reduxFormValues, props.values)

    props
      .createAdmin(formData, loggedInAs === 'Organization')
      .then(resp => {
        if (!resp || !resp.data || resp.status === 422) {
          // error toast is handled in Redux action
          props.setLoading(false)
          return
        }

        props.setLoading(false)
        if (loggedInAs === 'Organization') {
          props.history.push('/organization_admins')
        } else {
          props.history.push('/admins')
        }
      })
      .catch(() => props.setLoading(false))
  }

  useEffect(() => {
    if (has_api_access) {
      change('role_ids', [])
    }
  }, [has_api_access, change])

  const clientsOptions = useMemo(() => Object.values(props.clients), [props.clients])

  // portals of this client and not already selected
  const portalOptions = useMemo(() => {
    const clientPortals = Object.values(client_portals).map(portal => ({
      ...portal,
      value: portal.id,
      label: portal.name,
      user_groups: portal.user_groups
    }))

    return clientPortals
  }, [client_portals])

  // location options - remove any locations that are already selected

  const availableLocationOptions = useMemo(() => {
    if (!portals || !props.locations) {
      return emptyArray
    }

    const updatedLocations = props.locations.filter(location => {
      return portals.some(portalId => portalId === location.portal_id)
    })

    return updatedLocations
  }, [props.locations, portals])

  const emailValidation = useCallback(
    value => {
      if (!value) {
        return 'You must enter an email'
      }

      if (
        !/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
          value
        )
      ) {
        return 'You must enter a valid email'
      }

      if (loggedInAs === 'Organization' && !value.endsWith('@marketfuel.com')) {
        return 'You must enter a valid Marketfuel email'
      }
    },
    [loggedInAs]
  )

  return (
    <form onSubmit={props.handleSubmit(onSubmit)}>
      {props.errors ? (
        <div className="alert alert-danger" role="alert">
          An error has occurred
        </div>
      ) : null}
      {!(loggedInAs === 'Organization') && (
        <Card className="card-accent-primary">
          <CardHeader>
            <Icon.Key /> Enable / Disable API Access
          </CardHeader>
          <CardBody>
            <Row className="mb-1">
              <Col>
                <Field
                  name="has_api_access"
                  label="Has API Access"
                  component={Switch}
                  tooltipName="has_api_access"
                  tooltipText="If selected, this admin will have API access. Will not be able to select other User Roles."
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Field
                  name="has_agency_api_access"
                  label="Has Agency API Access"
                  component={Switch}
                  tooltipName="has_agency_api_access"
                  tooltipText="If selected, this admin will have Agency API access."
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {props.roles && (
        <Field
          name="role_ids"
          component={DropdownListNew2}
          options={props.roles}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id}
          textField="name"
          placeholder="Select User Role(s)"
          label="Admin Role(s):"
          tooltipName="User Role(s)"
          tooltipText="Select role(s) for the user"
          disabled={props.values.has_api_access}
          isMulti
        />
      )}
      <Field name="name" component={renderInput} label="* Admin Name: " placeholder="Enter name" input_type="text" />
      <Field
        name="email"
        component={renderInput}
        validate={emailValidation}
        label="* Email: "
        tooltipName="email"
        tooltipText="The email to be associated with your account. This will be used to sign in."
        input_type="text"
      />
      <Field name="user_security_field" component={renderInput} label="* Password: " input_type="password" />
      <Field
        name="user_security_field_confirmation"
        component={renderInput}
        label="* Password Confirmation: "
        input_type="password"
      />
      <Field
        name="timezone"
        component={renderTzSelect}
        options={timeZones()}
        label="Timezone"
        selectedTimezone={props.values.timezone?.value}
      />
      <br />
      <div className="tooltip_label">
        <label> Avatar: </label>
        {renderTooltip('avatar', 'Recommended Dimensions: 75x75px. Internal only.')}
      </div>

      <Field
        name="avatar"
        component={DropZoneField}
        type="file"
        imagefile={imageFile}
        handleOnDrop={handleOnDrop}
        label="Admin Avatar: "
        tooltipName="avatar"
        tooltipText="Recommended Dimensions: 75x75px. Internal only."
      />

      {imageFile && imageFile !== '' && (
        <div className="text-right">
          <MfButton onClick={clearImage} size="sm" className="text-right">
            {' '}
            Clear Image{' '}
          </MfButton>
        </div>
      )}

      <br />
      {loggedInAs === 'Organization' ? null : (
        <>
          <Card className="card-accent-primary">
            <CardHeader>Client Admin</CardHeader>
            <CardBody>
              <Field
                name="is_client_admin"
                label="Client Admin"
                component={Switch}
                tooltipName="is_client_admin"
                tooltipText="If selected, this admin will have access to special areas of the chosen client (i.e order approval), but restricted access to all other areas of the admin system"
              />

              {props.values?.is_client_admin ? (
                <>
                  <Field
                    name="client"
                    component={renderDropdownListNew}
                    options={clientsOptions}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    onBlur={e => e.preventDefault()} // Temp fix to prevent onBlur clearing
                    placeholder="Select Client"
                    label="* Client:"
                    tooltipName="client"
                    tooltipText="This is the client the user is part of, this will determine which portals the user can be allowed access to"
                  />
                  <hr /> {/* Add a horizontal line as a visual separation */}
                  {props.values.client && !emailExists && !emailAndClientExists ? (
                    <>
                      <p>
                        This email and client do not have a customer account. Please select the options below to create
                        a customer account.
                      </p>

                      <Field
                        name="portals"
                        component={DropdownListNew2}
                        isMulti
                        label="* Assign Portal(s)"
                        options={portalOptions}
                        getOptionLabel={option => option.label}
                        getOptionValue={option => option.id}
                        placeholder="Assign Portal to Customer"
                      />

                      <br />
                      <Field
                        name="locations"
                        component={DropdownListNew2}
                        label="* Assign Store View(s)"
                        options={availableLocationOptions}
                        isMulti
                        getOptionLabel={option => option.name}
                        getOptionValue={option => option.id}
                        placeholder="Assign Store View to Customer"
                        tooltipName="locations"
                        tooltipText="Select store views(s) for the user"
                      />
                      <br />
                      <Field
                        name="send_creation_email"
                        component={Switch}
                        label="Notify Customer Via Email"
                        disabled={disableCustomerNotification}
                      />

                      {disableCustomerNotification ? (
                        <Row>
                          <Col>
                            <div className="error">{customerNotificationMessage}</div>
                          </Col>
                        </Row>
                      ) : null}
                    </>
                  ) : (
                    emailExists &&
                    emailAndClientExists && (
                      <Field
                        name="linked_to_user"
                        label="Link this admin to the customer account with matching email and client"
                        component={Switch}
                        tooltipName="linked_to_user"
                        tooltipText="If enabled, this admin account will be associated with and granted access to the corresponding customer account"
                      />
                    )
                  )}
                </>
              ) : null}
            </CardBody>
          </Card>
          <br />
        </>
      )}
      <Card className="card-accent-primary">
        <CardHeader>Enable / Disable Account</CardHeader>
        <CardBody>
          <Field
            id="test"
            name="is_active"
            label="Active"
            value={true}
            checked={true}
            component={Switch}
            tooltipName="is_active"
            tooltipText="If selected, account will active for sign-in and other activities."
          />
        </CardBody>
      </Card>

      <br />
      <div style={{ float: 'right' }}>
        <BackButton />{' '}
        <MfButton type="submit" size="sm" color="success">
          {' '}
          Create Admin
        </MfButton>
      </div>
    </form>
  )
}

const validate = formValues => {
  const errors = {}
  const charLength = formValues.has_api_access ? { regex: 32, field: 32 } : { regex: '15,30', field: 'between 15 - 30' }
  const passwordRegex = new RegExp(`^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$%^&*_-])(?=.*?[0-9]).{${charLength.regex}}$`)

  if (!passwordRegex.test(formValues.user_security_field)) {
    errors.user_security_field = `Complexity requirement not met. Length should be ${charLength.field} characters and include: 1 lower case, 1 Upper case, 1 numeric and 1 special character`
  }

  if (formValues.user_security_field && !formValues.user_security_field_confirmation) {
    errors.user_security_field_confirmation = 'You must enter a password confirmation'
  }

  if (!formValues.name) {
    errors.name = 'You must enter a name'
  } else if (formValues.name.trim().split(/\s+/).length < 2) {
    errors.name = 'You must enter both a first name and a last name'
  }

  if (formValues.is_client_admin) {
    if (!formValues.client) {
      errors.client = 'You must select a client if client admin is checked'
    }
    if (formValues.client && (!formValues.portals || !formValues.portals.length)) {
      errors.portals = 'You must assign at least one portal'
    }

    if (formValues.client && (!formValues.locations || !formValues.locations.length)) {
      errors.locations = 'You must assign at least one location'
    }
  }

  if (formValues.has_api_access) {
    if (!formValues.is_client_admin) {
      errors.is_client_admin = 'You must check Client Admin if Admin has API access.'
    }
  }

  return errors
}

const mapStateToProps = state => {
  const selector = formValueSelector('adminForm') // Assuming 'adminForm' is your form name

  return {
    values: state.form.adminForm?.values || {},
    clients: state.clients,
    roles: state.roles ? Object.values(state.roles) : null,
    locations: state.locationsSlice?.options || [],
    client_portals: state.client_portals,
    sendCreationEmail: selector(state, 'send_creation_email'),
    // Add any other form values you need to access
    currentAdmin: state.admins.current_admin
  }
}
const mapDispatchToProps = {
  createUser,
  verifyUserByEmailAndClientId,
  createAdmin,
  fetchClientPortals,
  fetchLocationOptions,
  changeFieldValue: (form, field, value) => change(form, field, value)
}

const connectedAdminForm = connect(mapStateToProps, mapDispatchToProps)(AdminForm)

AdminForm.propTypes = {
  fetchClientPortals: PropTypes.func,
  createUser: PropTypes.func,
  createAdmin: PropTypes.func,
  setLoading: PropTypes.func,
  values: PropTypes.object,
  roles: PropTypes.array,
  clients: PropTypes.array,
  client_portals: PropTypes.array,
  locations: PropTypes.array,
  currentAdmin: PropTypes.object,
  history: PropTypes.object,
  handleSubmit: PropTypes.func,
  errors: PropTypes.array,
  change: PropTypes.func,
  changeFieldValue: PropTypes.func,
  fetchLocationOptions: PropTypes.func
}

export default withRouter(
  reduxForm({
    form: 'adminForm',
    validate
  })(connectedAdminForm)
)
